import {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';
import {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  TextInput as TextInputType,
} from 'react-native';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { QuickAction } from '@/experiences/sales-portal/components/quick-action-menu';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { useKeyboardHotkeysContext } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';

interface UseCellQuickActionsParams {
  disabled: boolean;
  editor: PriceEditor | undefined;
  handleStandardKeyPress: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => void;
  inputRef: RefObject<TextInputType | null>;
  onSave: (editor: PriceEditor, variantPackUUID: string) => void;
  quickActions: QuickAction[];
  throttle: (fn: () => void) => void;
  variantPackUUID: string;
}

interface UseCellQuickActionsResult {
  handleKeyPress: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  handleQuickActionSelect: (action: QuickAction) => void;
  isEditing: boolean;
  isQuickActionsOpen: boolean;
  /** Ref shadow for synchronous reads in handleBlur (state may be stale). */
  isQuickActionsOpenRef: MutableRefObject<boolean>;
  saveAndReset: () => void;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
}

/**
 * Shared quick-action state management for order entry input cells.
 *
 * Encapsulates the open/close state (+ ref shadow for blur-race workaround),
 * focus restoration, save-and-reset flow, Q-key toggle, and menu-open guard
 * that are identical across the price and GP% cells.
 */
export const useCellQuickActions = ({
  disabled,
  editor,
  handleStandardKeyPress,
  inputRef,
  onSave,
  quickActions,
  throttle,
  variantPackUUID,
}: UseCellQuickActionsParams): UseCellQuickActionsResult => {
  const { disableScope, enableScope } = useKeyboardHotkeysContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  // Ref shadows isQuickActionsOpen for synchronous reads in handleBlur.
  // The Dropdown overlay steals focus before React re-renders, so the
  // state value in handleBlur's closure is stale during that blur event.
  const isQuickActionsOpenRef = useRef(false);

  const closeQuickActions = useCallback(() => {
    isQuickActionsOpenRef.current = false;
    setIsQuickActionsOpen(false);
    disableScope(SalesPortalKeyboardScope.QUICK_ACTION_MENU);
    enableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
    // Restore focus to the input after the quick actions menu closes.
    // Throttled to the next animation frame so the dropdown unmount settles first.
    throttle(() => inputRef.current?.focus());
  }, [disableScope, enableScope, inputRef, throttle]);

  const openQuickActions = useCallback(() => {
    isQuickActionsOpenRef.current = true;
    setIsQuickActionsOpen(true);
    disableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
    enableScope(SalesPortalKeyboardScope.QUICK_ACTION_MENU);
  }, [disableScope, enableScope]);

  const saveAndReset = useCallback(() => {
    closeQuickActions();
    if (editor) {
      onSave(editor, variantPackUUID);
    }
    setIsEditing(false);
  }, [closeQuickActions, editor, onSave, variantPackUUID]);

  const handleQuickActionSelect = useCallback(
    (action: QuickAction) => {
      action.onSelect?.();
      saveAndReset();
    },
    [saveAndReset],
  );

  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      const key = e.nativeEvent.key;

      // Cell-specific: Q toggles quick action menu
      if (
        key.toLowerCase() === SalesPortalShortcuts.QuickActions &&
        !disabled &&
        quickActions.length > 0
      ) {
        e.preventDefault?.();
        if (isQuickActionsOpenRef.current) {
          saveAndReset();
        } else {
          openQuickActions();
        }
        return;
      }

      // When the menu is open, let QuickActionMenu's keyboard shortcuts
      // handle Enter, arrows, and Escape via the focused Pressable.
      // preventDefault stops the browser from scrolling the page on arrow keys.
      if (isQuickActionsOpenRef.current) {
        e.preventDefault?.();
        return;
      }

      // Delegate to shared standard shortcuts (/, r, l, c, Escape, arrows)
      handleStandardKeyPress(e);
    },
    [
      disabled,
      handleStandardKeyPress,
      openQuickActions,
      quickActions.length,
      saveAndReset,
    ],
  );

  return {
    handleKeyPress,
    handleQuickActionSelect,
    isEditing,
    isQuickActionsOpen,
    isQuickActionsOpenRef,
    saveAndReset,
    setIsEditing,
  };
};
